import React from "react";
import PropTypes from "prop-types";
import ClipLoader from "react-spinners/ClipLoader";
import { CCard } from "@coreui/react";
import { FaInfoCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import "./PatientDetailsCard.css";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import HealthStatusIndicator from "../../../../../../../components/HealthStatusIndicator/HealthStatusIndicator";
import { HEALTH_METRIC_TAGS, getHealthStatusByTag } from "../../../../../../config/thresholds";


const PatientDetailsCard = ({ title, details, loading, tooltipInfo }) => {
  const { t } = useTranslation();

  // Calculate BMI and get its health status
  const getBMIHealthStatus = () => {
    if (!details.weight || !details.height) {
      return 'neutral';
    }

    const bmi = details.weight / Math.pow(details.height / 100, 2);
    const gender = details.sex?.toLowerCase() === 'f' ? 'female' : 'male';
    return getHealthStatusByTag(bmi, HEALTH_METRIC_TAGS.BMI_VALUE, gender);
  };

  const bmiStatus = getBMIHealthStatus();

  // Calculate BMI value for display
  const calculateBMI = () => {
    if (!details.weight || !details.height) {
      return 'N/A';
    }
    const bmi = details.weight / Math.pow(details.height / 100, 2);
    return bmi.toFixed(1);
  };

  return (
    <CCard className="background-card-patient-details">
      {!loading && (
        <>
          <div className="div-row-tooltip-patient">
            <Tooltip
              title={<span className="tooltip-window">{tooltipInfo}</span>}
              arrow
            >
              <IconButton>
                <FaInfoCircle className="info-icon-data" />
              </IconButton>
            </Tooltip>
          </div>

          {/* Health Status Indicator for BMI */}
          {bmiStatus !== 'neutral' && (
            <HealthStatusIndicator 
              status={bmiStatus}
              size="medium"
              position="top-left"
              showBorder={true}
            />
          )}


          <h3>{title}</h3>
          <p>
            <strong>{t("name")}:</strong> {details.name}
          </p>
          <p>
            <strong>{t("surname")}:</strong> {details.surname}
          </p>
          <p>
            <strong>{t("fiscalCode")}:</strong> {details.fiscalCode}
          </p>
          <p>
            <strong>{t("weight")}:</strong> {details.weight} kg
          </p>
          <p>
            <strong>{t("height")}:</strong> {details.height} cm
          </p>
          <p className="bmi-display">
            <strong>{t("bmiValue")}:</strong> 
            <span className={`bmi-value health-status-${bmiStatus}`}>
              {calculateBMI()}
            </span>
          </p>
        </>
      )}
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px",
          }}
        >
          <ClipLoader
            color="#ffffff"
            loading={loading}
            size={50}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </div>
      )}
    </CCard>
  );
};

PatientDetailsCard.propTypes = {
  title: PropTypes.string.isRequired,
  details: PropTypes.shape({
    name: PropTypes.string.isRequired,
    surname: PropTypes.string.isRequired,
    fiscalCode: PropTypes.string.isRequired,
    weight: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    sex: PropTypes.string, // 'M' or 'F'
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  tooltipInfo: PropTypes.string.isRequired,
};

export default PatientDetailsCard;
